"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
/**
 * ClockCell - Cellule horloge ultra-personnalisable
 * 
 * Modes disponibles:
 * - 'clock' : Horloge temps réel
 * - 'countdown' : Compte à rebours vers une date
 * - 'stopwatch' : Chronomètre (temps écoulé depuis une date)
 * 
 * Styles disponibles:
 * - 'digital' : Affichage numérique
 * - 'analog' : Horloge à aiguilles
 * - 'flip' : Style flip clock rétro
 * - 'minimal' : Ultra minimaliste
 * - 'neon' : Style néon lumineux
 * - 'lcd' : Style LCD/calculatrice
 */const ClockCell = ({
  styles_properties = {},
  scaleFactor = 1,
  ...rest
}) => {
  const [currentTime, setCurrentTime] = (0, _react.useState)(new Date());
  const containerRef = (0, _react.useRef)(null);
  const contentRef = (0, _react.useRef)(null);
  const [fitScale, setFitScale] = (0, _react.useState)(null);
  const userScale = Number(styles_properties.scale);
  const contentScale = Number.isFinite(userScale) && userScale > 0 ? userScale : 1;

  // Configuration
  const mode = styles_properties.mode || 'clock'; // clock, countdown, stopwatch
  const style = styles_properties.style || 'digital'; // digital, analog, flip, minimal, neon, lcd
  const showSeconds = styles_properties.show_seconds !== false;
  const showDate = styles_properties.show_date || false;
  const showDay = styles_properties.show_day || false;
  const use24h = styles_properties.use_24h !== false;
  const showMilliseconds = styles_properties.show_milliseconds || false;
  const timezone = styles_properties.timezone || null; // null = local

  // Countdown/Stopwatch specific
  const targetDate = styles_properties.target_date ? new Date(styles_properties.target_date) : null;
  const countdownLabel = styles_properties.countdown_label || '';
  const showDays = styles_properties.show_days !== false;
  const showHours = styles_properties.show_hours !== false;
  const showMinutes = styles_properties.show_minutes !== false;
  const hideWhenComplete = styles_properties.hide_when_complete || false;
  const completeMessage = styles_properties.complete_message || '🎉 C\'est maintenant !';

  // Styling
  const primaryColor = styles_properties.primary_color || '#6366f1';
  const secondaryColor = styles_properties.secondary_color || '#8b5cf6';
  const backgroundColor = styles_properties.background_color || 'transparent';
  const textColor = styles_properties.text_color || '#ffffff';
  const accentColor = styles_properties.accent_color || '#22d3ee';
  const fontFamily = styles_properties.font_family || "'Inter', 'SF Pro Display', sans-serif";
  const glowIntensity = styles_properties.glow_intensity || 0; // 0-100
  const borderRadius = styles_properties.border_radius ?? 16;
  const showBackground = styles_properties.show_background !== false;
  const animate = styles_properties.animate !== false;

  // Update interval based on needs
  const updateInterval = showMilliseconds ? 50 : 1000;
  (0, _react.useEffect)(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, updateInterval);
    return () => clearInterval(timer);
  }, [updateInterval]);
  (0, _react.useLayoutEffect)(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;
    const updateFit = () => {
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      const iw = content.offsetWidth;
      const ih = content.offsetHeight;
      if (cw < 1 || ch < 1 || iw < 1 || ih < 1) return;
      const next = Math.min(cw / iw, ch / ih);
      if (Number.isFinite(next) && next > 0) {
        setFitScale(next);
      }
    };
    updateFit();
    const observer = new ResizeObserver(updateFit);
    observer.observe(container);
    observer.observe(content);
    return () => observer.disconnect();
  }, [mode, style, showSeconds, showMilliseconds, showDate, showDay, use24h, countdownLabel, showDays, showHours, showMinutes, hideWhenComplete, completeMessage, fontFamily]);

  // Get time in specified timezone
  const getDisplayTime = () => {
    if (timezone) {
      return new Date(currentTime.toLocaleString('en-US', {
        timeZone: timezone
      }));
    }
    return currentTime;
  };
  const displayTime = getDisplayTime();

  // Calculate countdown/stopwatch values
  const getTimeDiff = () => {
    if (!targetDate) return null;
    const diff = mode === 'countdown' ? targetDate.getTime() - currentTime.getTime() : currentTime.getTime() - targetDate.getTime();
    if (mode === 'countdown' && diff <= 0) {
      return {
        complete: true,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0
      };
    }
    const absDiff = Math.abs(diff);
    return {
      complete: false,
      days: Math.floor(absDiff / (1000 * 60 * 60 * 24)),
      hours: Math.floor(absDiff % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
      minutes: Math.floor(absDiff % (1000 * 60 * 60) / (1000 * 60)),
      seconds: Math.floor(absDiff % (1000 * 60) / 1000),
      milliseconds: Math.floor(absDiff % 1000 / 10)
    };
  };
  const timeDiff = mode === 'countdown' || mode === 'stopwatch' ? getTimeDiff() : null;
  const keyframes = `
        @keyframes clockPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
        }
        @keyframes clockGlow {
            0%, 100% { filter: drop-shadow(0 0 ${5 * scaleFactor}px ${primaryColor}80); }
            50% { filter: drop-shadow(0 0 ${15 * scaleFactor}px ${primaryColor}); }
        }
        @keyframes clockFlip {
            0% { transform: rotateX(0deg); }
            50% { transform: rotateX(-90deg); }
            100% { transform: rotateX(0deg); }
        }
        @keyframes clockSlide {
            0% { transform: translateY(-100%); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes neonFlicker {
            0%, 19%, 21%, 23%, 25%, 54%, 56%, 100% {
                text-shadow: 
                    0 0 ${4 * scaleFactor}px ${primaryColor},
                    0 0 ${11 * scaleFactor}px ${primaryColor},
                    0 0 ${19 * scaleFactor}px ${primaryColor},
                    0 0 ${40 * scaleFactor}px ${primaryColor};
            }
            20%, 24%, 55% {
                text-shadow: none;
            }
        }
        @keyframes secondHand {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
    `;

  // Render based on style
  const renderClock = () => {
    switch (style) {
      case 'analog':
        return /*#__PURE__*/(0, _jsxRuntime.jsx)(AnalogClock, {
          time: displayTime,
          scaleFactor: scaleFactor,
          primaryColor: primaryColor,
          secondaryColor: secondaryColor,
          accentColor: accentColor,
          textColor: textColor,
          showSeconds: showSeconds,
          showDate: showDate,
          showDay: showDay,
          glowIntensity: glowIntensity,
          animate: animate
        });
      case 'flip':
        return /*#__PURE__*/(0, _jsxRuntime.jsx)(FlipClock, {
          time: displayTime,
          scaleFactor: scaleFactor,
          primaryColor: primaryColor,
          secondaryColor: secondaryColor,
          textColor: textColor,
          showSeconds: showSeconds,
          use24h: use24h,
          animate: animate
        });
      case 'minimal':
        return /*#__PURE__*/(0, _jsxRuntime.jsx)(MinimalClock, {
          time: displayTime,
          scaleFactor: scaleFactor,
          primaryColor: primaryColor,
          textColor: textColor,
          showSeconds: showSeconds,
          use24h: use24h,
          showDate: showDate,
          fontFamily: fontFamily
        });
      case 'neon':
        return /*#__PURE__*/(0, _jsxRuntime.jsx)(NeonClock, {
          time: displayTime,
          scaleFactor: scaleFactor,
          primaryColor: primaryColor,
          secondaryColor: secondaryColor,
          showSeconds: showSeconds,
          use24h: use24h,
          animate: animate
        });
      case 'lcd':
        return /*#__PURE__*/(0, _jsxRuntime.jsx)(LCDClock, {
          time: displayTime,
          scaleFactor: scaleFactor,
          primaryColor: primaryColor,
          backgroundColor: backgroundColor,
          showSeconds: showSeconds,
          use24h: use24h,
          showDate: showDate
        });
      case 'digital':
      default:
        return /*#__PURE__*/(0, _jsxRuntime.jsx)(DigitalClock, {
          time: displayTime,
          scaleFactor: scaleFactor,
          primaryColor: primaryColor,
          secondaryColor: secondaryColor,
          textColor: textColor,
          showSeconds: showSeconds,
          showMilliseconds: showMilliseconds,
          use24h: use24h,
          showDate: showDate,
          showDay: showDay,
          fontFamily: fontFamily,
          glowIntensity: glowIntensity,
          animate: animate
        });
    }
  };
  const renderCountdown = () => {
    if (!timeDiff) return null;
    if (timeDiff.complete) {
      if (hideWhenComplete) return null;
      return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          fontSize: `${32 * scaleFactor}px`,
          fontWeight: 700,
          color: accentColor,
          textAlign: 'center',
          animation: animate ? 'clockPulse 1s ease-in-out infinite' : 'none'
        },
        children: completeMessage
      });
    }
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(CountdownDisplay, {
      timeDiff: timeDiff,
      label: countdownLabel,
      scaleFactor: scaleFactor,
      primaryColor: primaryColor,
      secondaryColor: secondaryColor,
      textColor: textColor,
      accentColor: accentColor,
      showDays: showDays,
      showHours: showHours,
      showMinutes: showMinutes,
      showSeconds: showSeconds,
      showMilliseconds: showMilliseconds,
      style: style,
      animate: animate,
      fontFamily: fontFamily,
      glowIntensity: glowIntensity
    });
  };
  const containerStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: showBackground ? style === 'neon' ? 'radial-gradient(ellipse at center, #1a0a2e 0%, #0a0a0f 100%)' : style === 'lcd' ? '#1a2f1a' : backgroundColor !== 'transparent' ? backgroundColor : `linear-gradient(135deg, ${primaryColor}10 0%, ${secondaryColor}05 100%)` : 'transparent',
    borderRadius: `${borderRadius * scaleFactor}px`,
    padding: `${8 * scaleFactor}px`,
    boxSizing: 'border-box',
    fontFamily: fontFamily,
    overflow: 'hidden'
  };
  const displayScale = (fitScale ?? 1) * contentScale;
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    ref: containerRef,
    style: containerStyle,
    ...rest,
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("style", {
      children: keyframes
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      ref: contentRef,
      style: {
        transform: `scale(${displayScale})`,
        transformOrigin: 'center center',
        flexShrink: 0,
        visibility: fitScale == null ? 'hidden' : 'visible'
      },
      children: [mode === 'clock' && renderClock(), (mode === 'countdown' || mode === 'stopwatch') && renderCountdown()]
    })]
  });
};

// ============ DIGITAL CLOCK ============
const DigitalClock = ({
  time,
  scaleFactor,
  primaryColor,
  secondaryColor,
  textColor,
  showSeconds,
  showMilliseconds,
  use24h,
  showDate,
  showDay,
  fontFamily,
  glowIntensity,
  animate
}) => {
  const hours = use24h ? time.getHours() : time.getHours() % 12 || 12;
  const minutes = time.getMinutes().toString().padStart(2, '0');
  const seconds = time.getSeconds().toString().padStart(2, '0');
  const milliseconds = Math.floor(time.getMilliseconds() / 10).toString().padStart(2, '0');
  const ampm = time.getHours() >= 12 ? 'PM' : 'AM';
  const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const glowStyle = glowIntensity > 0 ? {
    textShadow: `0 0 ${glowIntensity * 0.5 * scaleFactor}px ${primaryColor}`
  } : {};
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      textAlign: 'center'
    },
    children: [showDay && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        fontSize: `${18 * scaleFactor}px`,
        fontWeight: 500,
        color: `${textColor}99`,
        marginBottom: `${8 * scaleFactor}px`,
        textTransform: 'uppercase',
        letterSpacing: `${2 * scaleFactor}px`
      },
      children: days[time.getDay()]
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'center',
        gap: `${4 * scaleFactor}px`,
        ...glowStyle
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${72 * scaleFactor}px`,
          fontWeight: 700,
          fontFamily: fontFamily,
          color: textColor,
          fontVariantNumeric: 'tabular-nums'
        },
        children: hours.toString().padStart(2, '0')
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${72 * scaleFactor}px`,
          fontWeight: 300,
          color: primaryColor,
          animation: animate ? 'clockPulse 1s ease-in-out infinite' : 'none'
        },
        children: ":"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${72 * scaleFactor}px`,
          fontWeight: 700,
          fontFamily: fontFamily,
          color: textColor,
          fontVariantNumeric: 'tabular-nums'
        },
        children: minutes
      }), showSeconds && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          style: {
            fontSize: `${72 * scaleFactor}px`,
            fontWeight: 300,
            color: primaryColor,
            animation: animate ? 'clockPulse 1s ease-in-out infinite' : 'none'
          },
          children: ":"
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          style: {
            fontSize: `${72 * scaleFactor}px`,
            fontWeight: 700,
            fontFamily: fontFamily,
            color: textColor,
            fontVariantNumeric: 'tabular-nums'
          },
          children: seconds
        })]
      }), showMilliseconds && /*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
        style: {
          fontSize: `${36 * scaleFactor}px`,
          fontWeight: 400,
          color: `${textColor}80`,
          marginLeft: `${4 * scaleFactor}px`,
          fontVariantNumeric: 'tabular-nums'
        },
        children: [".", milliseconds]
      }), !use24h && /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: `${24 * scaleFactor}px`,
          fontWeight: 600,
          color: primaryColor,
          marginLeft: `${8 * scaleFactor}px`
        },
        children: ampm
      })]
    }), showDate && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        fontSize: `${16 * scaleFactor}px`,
        color: `${textColor}80`,
        marginTop: `${12 * scaleFactor}px`,
        fontWeight: 500
      },
      children: [time.getDate(), " ", months[time.getMonth()], " ", time.getFullYear()]
    })]
  });
};

// ============ ANALOG CLOCK ============
const AnalogClock = ({
  time,
  scaleFactor,
  primaryColor,
  secondaryColor,
  accentColor,
  textColor,
  showSeconds,
  showDate,
  showDay,
  glowIntensity,
  animate
}) => {
  const size = 200 * scaleFactor;
  const center = size / 2;
  const hours = time.getHours() % 12;
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();
  const hourAngle = (hours + minutes / 60) * 30 - 90;
  const minuteAngle = (minutes + seconds / 60) * 6 - 90;
  const secondAngle = seconds * 6 - 90;
  const days = ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM'];
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      textAlign: 'center'
    },
    children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("svg", {
      width: size,
      height: size,
      viewBox: `0 0 ${size} ${size}`,
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("circle", {
        cx: center,
        cy: center,
        r: center - 5 * scaleFactor,
        fill: "none",
        stroke: `${primaryColor}30`,
        strokeWidth: 2 * scaleFactor
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("circle", {
        cx: center,
        cy: center,
        r: center - 10 * scaleFactor,
        fill: `${primaryColor}10`,
        stroke: primaryColor,
        strokeWidth: 1 * scaleFactor,
        style: {
          filter: glowIntensity > 0 ? `drop-shadow(0 0 ${glowIntensity * 0.3}px ${primaryColor})` : 'none'
        }
      }), [...Array(12)].map((_, i) => {
        const angle = (i * 30 - 90) * (Math.PI / 180);
        const isMain = i % 3 === 0;
        const outerR = center - 15 * scaleFactor;
        const innerR = center - (isMain ? 30 : 25) * scaleFactor;
        return /*#__PURE__*/(0, _jsxRuntime.jsx)("line", {
          x1: center + Math.cos(angle) * innerR,
          y1: center + Math.sin(angle) * innerR,
          x2: center + Math.cos(angle) * outerR,
          y2: center + Math.sin(angle) * outerR,
          stroke: isMain ? textColor : `${textColor}60`,
          strokeWidth: (isMain ? 3 : 1.5) * scaleFactor,
          strokeLinecap: "round"
        }, i);
      }), [12, 3, 6, 9].map(num => {
        const angle = ((num === 12 ? 0 : num) * 30 - 90) * (Math.PI / 180);
        const r = center - 45 * scaleFactor;
        return /*#__PURE__*/(0, _jsxRuntime.jsx)("text", {
          x: center + Math.cos(angle) * r,
          y: center + Math.sin(angle) * r,
          fill: textColor,
          fontSize: 14 * scaleFactor,
          fontWeight: "600",
          textAnchor: "middle",
          dominantBaseline: "central",
          children: num
        }, num);
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("line", {
        x1: center,
        y1: center,
        x2: center + Math.cos(hourAngle * Math.PI / 180) * (center - 60 * scaleFactor),
        y2: center + Math.sin(hourAngle * Math.PI / 180) * (center - 60 * scaleFactor),
        stroke: textColor,
        strokeWidth: 6 * scaleFactor,
        strokeLinecap: "round"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("line", {
        x1: center,
        y1: center,
        x2: center + Math.cos(minuteAngle * Math.PI / 180) * (center - 35 * scaleFactor),
        y2: center + Math.sin(minuteAngle * Math.PI / 180) * (center - 35 * scaleFactor),
        stroke: primaryColor,
        strokeWidth: 4 * scaleFactor,
        strokeLinecap: "round"
      }), showSeconds && /*#__PURE__*/(0, _jsxRuntime.jsx)("line", {
        x1: center - Math.cos(secondAngle * Math.PI / 180) * (20 * scaleFactor),
        y1: center - Math.sin(secondAngle * Math.PI / 180) * (20 * scaleFactor),
        x2: center + Math.cos(secondAngle * Math.PI / 180) * (center - 25 * scaleFactor),
        y2: center + Math.sin(secondAngle * Math.PI / 180) * (center - 25 * scaleFactor),
        stroke: accentColor,
        strokeWidth: 2 * scaleFactor,
        strokeLinecap: "round",
        style: {
          transition: animate ? 'none' : 'transform 0.1s ease-out'
        }
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("circle", {
        cx: center,
        cy: center,
        r: 8 * scaleFactor,
        fill: primaryColor
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("circle", {
        cx: center,
        cy: center,
        r: 4 * scaleFactor,
        fill: accentColor
      })]
    }), (showDate || showDay) && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        marginTop: `${12 * scaleFactor}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: `${12 * scaleFactor}px`
      },
      children: [showDay && /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          padding: `${4 * scaleFactor}px ${10 * scaleFactor}px`,
          background: `${primaryColor}20`,
          borderRadius: `${6 * scaleFactor}px`,
          fontSize: `${12 * scaleFactor}px`,
          fontWeight: 600,
          color: primaryColor
        },
        children: days[time.getDay()]
      }), showDate && /*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
        style: {
          fontSize: `${14 * scaleFactor}px`,
          color: `${textColor}80`,
          fontWeight: 500
        },
        children: [time.getDate().toString().padStart(2, '0'), "/", (time.getMonth() + 1).toString().padStart(2, '0')]
      })]
    })]
  });
};

// ============ FLIP CLOCK ============
const FlipClock = ({
  time,
  scaleFactor,
  primaryColor,
  secondaryColor,
  textColor,
  showSeconds,
  use24h,
  animate
}) => {
  const hours = use24h ? time.getHours() : time.getHours() % 12 || 12;
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();
  const FlipCard = ({
    value,
    label
  }) => /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      textAlign: 'center'
    },
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        display: 'flex',
        gap: `${4 * scaleFactor}px`
      },
      children: value.toString().padStart(2, '0').split('').map((digit, i) => /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          width: `${50 * scaleFactor}px`,
          height: `${70 * scaleFactor}px`,
          background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 50%, #2a2a2a 50%, #1a1a1a 100%)',
          borderRadius: `${8 * scaleFactor}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: `${48 * scaleFactor}px`,
          fontWeight: 700,
          color: textColor,
          fontFamily: "'SF Mono', 'Fira Code', monospace",
          boxShadow: `0 ${4 * scaleFactor}px ${15 * scaleFactor}px rgba(0,0,0,0.4)`,
          position: 'relative',
          overflow: 'hidden'
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          style: {
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: `${1 * scaleFactor}px`,
            background: 'rgba(0,0,0,0.5)'
          }
        }), digit]
      }, i))
    }), label && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        marginTop: `${8 * scaleFactor}px`,
        fontSize: `${10 * scaleFactor}px`,
        fontWeight: 600,
        color: `${textColor}60`,
        textTransform: 'uppercase',
        letterSpacing: `${2 * scaleFactor}px`
      },
      children: label
    })]
  });
  const Separator = () => /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      display: 'flex',
      flexDirection: 'column',
      gap: `${12 * scaleFactor}px`,
      padding: `0 ${8 * scaleFactor}px`
    },
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        width: `${10 * scaleFactor}px`,
        height: `${10 * scaleFactor}px`,
        borderRadius: '50%',
        background: primaryColor,
        animation: animate ? 'clockPulse 1s ease-in-out infinite' : 'none'
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        width: `${10 * scaleFactor}px`,
        height: `${10 * scaleFactor}px`,
        borderRadius: '50%',
        background: primaryColor,
        animation: animate ? 'clockPulse 1s ease-in-out infinite' : 'none'
      }
    })]
  });
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: `${8 * scaleFactor}px`
    },
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(FlipCard, {
      value: hours,
      label: "heures"
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(Separator, {}), /*#__PURE__*/(0, _jsxRuntime.jsx)(FlipCard, {
      value: minutes,
      label: "minutes"
    }), showSeconds && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(Separator, {}), /*#__PURE__*/(0, _jsxRuntime.jsx)(FlipCard, {
        value: seconds,
        label: "secondes"
      })]
    })]
  });
};

// ============ MINIMAL CLOCK ============
const MinimalClock = ({
  time,
  scaleFactor,
  primaryColor,
  textColor,
  showSeconds,
  use24h,
  showDate,
  fontFamily
}) => {
  const hours = use24h ? time.getHours() : time.getHours() % 12 || 12;
  const minutes = time.getMinutes().toString().padStart(2, '0');
  const seconds = time.getSeconds().toString().padStart(2, '0');
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      textAlign: 'center'
    },
    children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        fontSize: `${96 * scaleFactor}px`,
        fontWeight: 200,
        fontFamily: fontFamily,
        color: textColor,
        letterSpacing: `-${4 * scaleFactor}px`,
        lineHeight: 1
      },
      children: [hours.toString().padStart(2, '0'), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          color: primaryColor
        },
        children: ":"
      }), minutes, showSeconds && /*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
        style: {
          fontSize: `${48 * scaleFactor}px`,
          color: `${textColor}50`,
          fontWeight: 300
        },
        children: [":", seconds]
      })]
    }), showDate && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        marginTop: `${16 * scaleFactor}px`,
        fontSize: `${14 * scaleFactor}px`,
        fontWeight: 400,
        color: `${textColor}40`,
        letterSpacing: `${4 * scaleFactor}px`,
        textTransform: 'uppercase'
      },
      children: time.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      })
    })]
  });
};

// ============ NEON CLOCK ============
const NeonClock = ({
  time,
  scaleFactor,
  primaryColor,
  secondaryColor,
  showSeconds,
  use24h,
  animate
}) => {
  const hours = use24h ? time.getHours() : time.getHours() % 12 || 12;
  const minutes = time.getMinutes().toString().padStart(2, '0');
  const seconds = time.getSeconds().toString().padStart(2, '0');
  const ampm = time.getHours() >= 12 ? 'PM' : 'AM';
  const neonStyle = {
    textShadow: `
            0 0 ${5 * scaleFactor}px ${primaryColor},
            0 0 ${10 * scaleFactor}px ${primaryColor},
            0 0 ${20 * scaleFactor}px ${primaryColor},
            0 0 ${40 * scaleFactor}px ${primaryColor}
        `
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      textAlign: 'center'
    },
    children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `${80 * scaleFactor}px`,
        fontWeight: 400,
        fontFamily: "'Orbitron', 'Audiowide', sans-serif",
        color: primaryColor,
        ...neonStyle,
        animation: animate ? 'neonFlicker 3s infinite' : 'none'
      },
      children: [hours.toString().padStart(2, '0'), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          margin: `0 ${8 * scaleFactor}px`,
          animation: animate ? 'clockPulse 1s ease-in-out infinite' : 'none'
        },
        children: ":"
      }), minutes, showSeconds && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          style: {
            margin: `0 ${8 * scaleFactor}px`,
            animation: animate ? 'clockPulse 1s ease-in-out infinite' : 'none'
          },
          children: ":"
        }), seconds]
      })]
    }), !use24h && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        marginTop: `${12 * scaleFactor}px`,
        fontSize: `${24 * scaleFactor}px`,
        fontFamily: "'Orbitron', sans-serif",
        color: secondaryColor,
        textShadow: `
                        0 0 ${5 * scaleFactor}px ${secondaryColor},
                        0 0 ${10 * scaleFactor}px ${secondaryColor}
                    `
      },
      children: ampm
    })]
  });
};

// ============ LCD CLOCK ============
const LCDClock = ({
  time,
  scaleFactor,
  primaryColor,
  showSeconds,
  use24h,
  showDate
}) => {
  const hours = use24h ? time.getHours() : time.getHours() % 12 || 12;
  const minutes = time.getMinutes().toString().padStart(2, '0');
  const seconds = time.getSeconds().toString().padStart(2, '0');
  const lcdColor = '#90EE90'; // Classic LCD green

  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      padding: `${20 * scaleFactor}px ${30 * scaleFactor}px`,
      background: '#1a2f1a',
      borderRadius: `${12 * scaleFactor}px`,
      border: `${3 * scaleFactor}px solid #2a4a2a`,
      boxShadow: `inset 0 0 ${20 * scaleFactor}px rgba(0,0,0,0.5)`
    },
    children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        fontFamily: "'DSEG7 Classic', 'Segment7', 'Courier New', monospace",
        fontSize: `${64 * scaleFactor}px`,
        color: lcdColor,
        textShadow: `0 0 ${10 * scaleFactor}px ${lcdColor}40`,
        letterSpacing: `${4 * scaleFactor}px`,
        fontVariantNumeric: 'tabular-nums'
      },
      children: [hours.toString().padStart(2, '0'), ":", minutes, showSeconds ? `:${seconds}` : '']
    }), showDate && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        marginTop: `${12 * scaleFactor}px`,
        fontFamily: "'Courier New', monospace",
        fontSize: `${14 * scaleFactor}px`,
        color: `${lcdColor}80`,
        textAlign: 'center'
      },
      children: time.toLocaleDateString('fr-FR')
    })]
  });
};

// ============ COUNTDOWN DISPLAY ============
const CountdownDisplay = ({
  timeDiff,
  label,
  scaleFactor,
  primaryColor,
  secondaryColor,
  textColor,
  accentColor,
  showDays,
  showHours,
  showMinutes,
  showSeconds,
  showMilliseconds,
  style,
  animate,
  fontFamily,
  glowIntensity
}) => {
  const TimeBlock = ({
    value,
    unit
  }) => {
    if (style === 'flip') {
      return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          textAlign: 'center'
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          style: {
            display: 'flex',
            gap: `${4 * scaleFactor}px`
          },
          children: value.toString().padStart(2, '0').split('').map((digit, i) => /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
            style: {
              width: `${45 * scaleFactor}px`,
              height: `${60 * scaleFactor}px`,
              background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 50%, #2a2a2a 50%, #1a1a1a 100%)',
              borderRadius: `${6 * scaleFactor}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: `${36 * scaleFactor}px`,
              fontWeight: 700,
              color: textColor,
              boxShadow: `0 ${3 * scaleFactor}px ${10 * scaleFactor}px rgba(0,0,0,0.3)`
            },
            children: digit
          }, i))
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          style: {
            marginTop: `${8 * scaleFactor}px`,
            fontSize: `${11 * scaleFactor}px`,
            fontWeight: 600,
            color: `${textColor}60`,
            textTransform: 'uppercase'
          },
          children: unit
        })]
      });
    }
    if (style === 'neon') {
      return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          textAlign: 'center'
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          style: {
            fontSize: `${56 * scaleFactor}px`,
            fontWeight: 400,
            fontFamily: "'Orbitron', sans-serif",
            color: primaryColor,
            textShadow: `
                            0 0 ${5 * scaleFactor}px ${primaryColor},
                            0 0 ${10 * scaleFactor}px ${primaryColor},
                            0 0 ${20 * scaleFactor}px ${primaryColor}
                        `
          },
          children: value.toString().padStart(2, '0')
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          style: {
            fontSize: `${12 * scaleFactor}px`,
            color: secondaryColor,
            textTransform: 'uppercase',
            textShadow: `0 0 ${5 * scaleFactor}px ${secondaryColor}`
          },
          children: unit
        })]
      });
    }

    // Default digital style
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        textAlign: 'center'
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          fontSize: `${56 * scaleFactor}px`,
          fontWeight: 700,
          fontFamily: fontFamily,
          color: textColor,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          textShadow: glowIntensity > 0 ? `0 0 ${glowIntensity * 0.3 * scaleFactor}px ${primaryColor}` : 'none'
        },
        children: value.toString().padStart(2, '0')
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          marginTop: `${6 * scaleFactor}px`,
          fontSize: `${12 * scaleFactor}px`,
          fontWeight: 600,
          color: primaryColor,
          textTransform: 'uppercase',
          letterSpacing: `${1 * scaleFactor}px`
        },
        children: unit
      })]
    });
  };
  const Separator = () => /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    style: {
      fontSize: `${40 * scaleFactor}px`,
      fontWeight: 300,
      color: `${textColor}40`,
      padding: `0 ${8 * scaleFactor}px`
    },
    children: ":"
  });
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      textAlign: 'center'
    },
    children: [label && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        fontSize: `${16 * scaleFactor}px`,
        fontWeight: 600,
        color: `${textColor}80`,
        marginBottom: `${20 * scaleFactor}px`,
        textTransform: 'uppercase',
        letterSpacing: `${2 * scaleFactor}px`
      },
      children: label
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: `${style === 'flip' ? 12 : 4} * ${scaleFactor}px`
      },
      children: [showDays && timeDiff.days > 0 && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(TimeBlock, {
          value: timeDiff.days,
          unit: "jours"
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(Separator, {})]
      }), showHours && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(TimeBlock, {
          value: timeDiff.hours,
          unit: "heures"
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(Separator, {})]
      }), showMinutes && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(TimeBlock, {
          value: timeDiff.minutes,
          unit: "min"
        }), showSeconds && /*#__PURE__*/(0, _jsxRuntime.jsx)(Separator, {})]
      }), showSeconds && /*#__PURE__*/(0, _jsxRuntime.jsx)(TimeBlock, {
        value: timeDiff.seconds,
        unit: "sec"
      }), showMilliseconds && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          fontSize: `${24 * scaleFactor}px`,
          fontWeight: 400,
          color: `${textColor}60`,
          alignSelf: 'flex-end',
          marginBottom: `${20 * scaleFactor}px`,
          fontVariantNumeric: 'tabular-nums'
        },
        children: [".", timeDiff.milliseconds.toString().padStart(2, '0')]
      })]
    })]
  });
};
var _default = exports.default = ClockCell;